import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock, ChevronLeft, AlertCircle, HelpCircle } from "lucide-react";
import { getPost, POSTS } from "@/blog/posts";
import SEO from "@/components/SEO";

function Section({ s }) {
  if (s.kind === "lede") {
    return <p className="text-xl lg:text-2xl font-display font-medium leading-snug text-zinc-800 mb-10">{s.body}</p>;
  }
  if (s.kind === "h2") {
    return <h2 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight mt-12 mb-5 text-zinc-900">{s.text}</h2>;
  }
  if (s.kind === "p") {
    return <p className="text-base lg:text-lg text-zinc-700 leading-relaxed mb-6">{s.text}</p>;
  }
  if (s.kind === "list") {
    return (
      <ul className="space-y-4 mb-8 mt-2">
        {s.items.map((it, i) => (
          <li key={i} className="flex gap-4">
            <span className="font-mono text-xs text-[#EF4444] mt-1.5 shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <div className="font-display font-bold text-zinc-900 text-base lg:text-lg">{it.t}</div>
              <div className="text-zinc-600 text-sm lg:text-base leading-relaxed mt-1">{it.d}</div>
            </div>
          </li>
        ))}
      </ul>
    );
  }
  if (s.kind === "callout") {
    return (
      <aside className="my-10 bg-zinc-50 border-l-4 border-[#EF4444] p-6 lg:p-8">
        <div className="inline-flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-[#EF4444]" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#EF4444]">◷ {s.title}</span>
        </div>
        <p className="text-zinc-700 text-base leading-relaxed">{s.body}</p>
      </aside>
    );
  }
  if (s.kind === "data-table") {
    return (
      <div className="my-10 overflow-x-auto">
        <table className="w-full border border-zinc-200 text-sm" data-testid="blog-data-table">
          <thead>
            <tr className="bg-zinc-900 text-white">
              <th className="text-left px-4 lg:px-6 py-3 font-mono text-[11px] uppercase tracking-widest font-normal">RESUMELAB SCORE</th>
              <th className="text-left px-4 lg:px-6 py-3 font-mono text-[11px] uppercase tracking-widest font-normal">CALLBACK RATE</th>
              <th className="text-left px-4 lg:px-6 py-3 font-mono text-[11px] uppercase tracking-widest font-normal">VS BASELINE</th>
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r, i) => (
              <tr key={i} className={`border-t border-zinc-100 ${i === s.rows.length - 1 ? "bg-[#EF4444]/5" : ""}`}>
                <td className="px-4 lg:px-6 py-3 font-medium text-zinc-900">{r.range}</td>
                <td className="px-4 lg:px-6 py-3 font-display font-extrabold text-zinc-900">{r.callback}</td>
                <td className="px-4 lg:px-6 py-3 text-[#EF4444] font-mono tracking-wider text-xs">{r.lift}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (s.kind === "cta") {
    return (
      <div className="my-12 bg-zinc-900 text-white p-8 lg:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="font-display font-extrabold text-xl lg:text-2xl tracking-tight max-w-md">{s.text}</div>
        <Link
          to={s.href}
          data-testid="blog-post-cta"
          className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white self-start sm:self-auto"
        >
          Start audit <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const pretty = new Date(post.publishedAt).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  // Estimate word count for SEO schema (helps Google rank long-form content)
  const wordCount = post.sections.reduce((n, s) => {
    if (s.body) return n + s.body.split(/\s+/).length;
    if (s.text) return n + s.text.split(/\s+/).length;
    if (s.items) return n + s.items.reduce((m, it) => m + (it.t + " " + (it.d || "")).split(/\s+/).length, 0);
    return n;
  }, 0);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.description,
        "datePublished": post.publishedAt,
        "dateModified": post.updatedAt || post.publishedAt,
        "wordCount": wordCount,
        "articleSection": post.tags[0],
        "keywords": post.tags.join(", "),
        "inLanguage": "en",
        "author": { "@type": "Organization", "name": "ResumeLab", "url": "https://resumelab.co.ke" },
        "publisher": {
          "@type": "Organization",
          "name": "ResumeLab",
          "logo": { "@type": "ImageObject", "url": "https://resumelab.co.ke/og-image.svg" },
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://resumelab.co.ke/blog/${post.slug}`,
        },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://resumelab.co.ke/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://resumelab.co.ke/blog" },
          { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://resumelab.co.ke/blog/${post.slug}` },
        ],
      },
      ...(post.faqs && post.faqs.length > 0 ? [{
        "@type": "FAQPage",
        "mainEntity": post.faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      }] : []),
    ],
  };

  // "More posts" picker (everything else)
  const other = POSTS.filter((p) => p.slug !== post.slug);

  return (
    <main className="text-zinc-900 bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={articleJsonLd}
      />

      {/* HERO */}
      <section className={`bg-gradient-to-br ${post.heroAccent} text-white border-b border-zinc-200`}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-16 lg:pb-20">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-white/70 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            BACK TO BLOG
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-3 mb-6">
            {post.tags.map((t) => (
              <span key={t} className="font-mono text-[10px] uppercase tracking-widest text-white/80 border border-white/30 px-2.5 py-1">
                {t}
              </span>
            ))}
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-6xl tracking-tight leading-[1.0]">
            {post.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-xs lg:text-sm text-white/80 font-mono uppercase tracking-wider">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{pretty}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{post.readingMinutes} MIN READ</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-8 lg:py-12" data-testid="blog-post-body">
        {post.sections.map((s, i) => (
          <Section key={i} s={s} />
        ))}
      </article>

      {/* FAQ — visible + structured for FAQPage JSON-LD rich snippets */}
      {post.faqs && post.faqs.length > 0 ? (
        <section className="border-t border-zinc-200 bg-white" data-testid="blog-faq-section">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="w-4 h-4 text-[#EF4444]" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#EF4444]">◷ FREQUENTLY ASKED</span>
            </div>
            <h2 className="font-display font-extrabold text-2xl lg:text-4xl tracking-tight text-zinc-900 mb-10">
              Quick answers
            </h2>
            <div className="space-y-8">
              {post.faqs.map((f, i) => (
                <div
                  key={i}
                  data-testid={`blog-faq-item-${i}`}
                  className="border-l-2 border-zinc-200 pl-5 lg:pl-7"
                >
                  <div className="font-display font-bold text-zinc-900 text-base lg:text-lg leading-snug mb-3">
                    {f.q}
                  </div>
                  <div className="text-zinc-600 text-sm lg:text-base leading-relaxed">
                    {f.a}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* MORE POSTS */}
      {other.length > 0 ? (
        <section className="border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
            <div className="overline mb-6">◇ KEEP READING</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {other.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group block bg-white border border-zinc-200 hover:border-zinc-900 transition-colors p-6"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">◷ {p.tags[0]}</div>
                  <div className="font-display font-bold text-lg text-zinc-900 leading-snug group-hover:text-[#EF4444] transition-colors">
                    {p.title}
                  </div>
                  <div className="mt-3 text-xs font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                    {p.readingMinutes} MIN <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </main>
  );
}
